<script setup>
// Astra DS · Volumen — el volumen de la emisión, que se despliega.
//
// Figma `Pattern/Volumen 2536:30934`, `Estado=cerrado 2536:30922` /
// `Estado=abierto 2536:30925`. Entregas `2026-10-20-player-48-y-volumen.md`
// (Radio) y `2026-10-21-portada-franja-y-player-en-cabecera.md` (la cabecera,
// «con el mismo comportamiento que en Radio»).
//
// Sale de `NowPlayingFoto` el 21.10.26 para que lo compartan los dos players.
// El nivel es el del reproductor (`stores/player.js`, `volumen`) y lo aplica
// `App.vue` sobre la emisión; esto solo lo enseña y lo cambia (`v-model`).
import { computed, ref, nextTick, onMounted, onBeforeUnmount } from "vue";
import AstraIcon from "@/components/icons/AstraIcon.vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const props = defineProps({
  // De 0 a 1.
  modelValue: { type: Number, default: 1 },
  // El alto de la celda del icono: 48 junto al transporte de Radio, 46 en la
  // cabecera. El ancho lo pide cada sitio por CSS.
  alto: { type: Number, default: 48 },
  // Hacia dónde se despliega la línea: `arriba` en Radio, `abajo` en la cabecera,
  // donde hacia arriba se saldría de la pantalla.
  abre: {
    type: String,
    default: "arriba",
    validator: (v) => ["arriba", "abajo"].includes(v),
  },
});

// Un id por instancia: la cabecera y Radio pueden estar en la misma página.
let cuenta = 0;
const idLinea = `volumen-linea-${++cuenta}-${Math.random().toString(36).slice(2, 7)}`;

const emit = defineEmits(["update:modelValue"]);

// ── Volumen ──
//
// `Pattern/Volumen 2536:30934`, entrega `2026-10-20-player-48-y-volumen.md`.
// 🔴 **Solo el icono; la línea se despliega al pulsarlo** (Pablo, 20.10.26: «la
// zona de interacción es muy pequeña»). Encima del icono, a 8, flota la línea de
// 150 de recorrido con el nivel en número. Se recoge al soltar después de
// ajustar, al pulsar otra vez el icono, al pulsar fuera y con Esc o Intro. Con
// teclado, al abrir el foco pasa a la línea y al cerrar vuelve al icono.
const volumenAbierto = ref(false);
const volumenCaja = ref(null);
const volumenIcono = ref(null);
const volumenLinea = ref(null);
const nivel = computed(() => Math.round(props.modelValue * 100));
const porcentaje = computed(() => `${nivel.value}%`);

// Hacia abajo, la caja va en `position: fixed` bajo el icono: en la cabecera,
// que lleva `overflow: hidden`, colgando por debajo quedaba recortada.
const posicionFija = ref(null);
function colocar() {
  if (props.abre !== "abajo" || !volumenIcono.value) {
    posicionFija.value = null;
    return;
  }
  const r = volumenIcono.value.getBoundingClientRect();
  // 🔴 Desde el 04.11.26 el icono no toca el filete de la cabecera: entre los
  // dos hay 12 de aire. La caja cuelga, como antes, a 8 del filete.
  const cabecera = volumenIcono.value.closest(".site-header");
  const suelo = cabecera ? cabecera.getBoundingClientRect().bottom : r.bottom;
  posicionFija.value = {
    position: "fixed",
    top: `${Math.round(suelo + 8)}px`,
    left: `${Math.round(r.left + r.width / 2)}px`,
    bottom: "auto",
  };
}

async function abrirVolumen() {
  colocar();
  volumenAbierto.value = true;
  await nextTick();
  volumenLinea.value?.focus();
}

function cerrarVolumen({ foco = true } = {}) {
  if (!volumenAbierto.value) return;
  volumenAbierto.value = false;
  if (foco) volumenIcono.value?.focus();
}

const alternarVolumen = () => (volumenAbierto.value ? cerrarVolumen() : abrirVolumen());

function alPulsarFuera(e) {
  if (volumenAbierto.value && !volumenCaja.value?.contains(e.target)) cerrarVolumen({ foco: false });
}

function alTeclearEnLinea(e) {
  if (e.key === "Escape" || e.key === "Enter") {
    e.preventDefault();
    cerrarVolumen();
  }
}

function alTeclear(e) {
  if (e.key === "Escape") cerrarVolumen();
}

onMounted(() => {
  document.addEventListener("pointerdown", alPulsarFuera);
  document.addEventListener("keydown", alTeclear);
});
onBeforeUnmount(() => {
  document.removeEventListener("pointerdown", alPulsarFuera);
  document.removeEventListener("keydown", alTeclear);
});
</script>

<template>
  <div
    ref="volumenCaja"
    class="volumen"
    :class="`volumen--${abre}`"
    :style="{ '--alto-celda': `${alto}px` }"
  >
    <button
      ref="volumenIcono"
      type="button"
      class="volumen__icono"
      :aria-label="t('marco.emision.volumenNivel', { nivel })"
      :aria-expanded="volumenAbierto ? 'true' : 'false'"
      :aria-controls="idLinea"
      @click="alternarVolumen"
    >
      <AstraIcon name="volume-high" :size="22" />
    </button>
    <!-- La línea, encima del icono. `change` llega al soltar: ahí se recoge.
         Con teclado, `change` llega a cada flecha, así que solo se recoge
         con Esc o Intro. -->
    <div v-show="volumenAbierto" :id="idLinea" class="volumen__caja" :style="posicionFija">
      <p class="volumen__numero" aria-hidden="true">{{ nivel }}</p>
      <input
        ref="volumenLinea"
        class="volumen__linea"
        type="range"
        min="0"
        max="1"
        step="0.05"
        :value="modelValue"
        :style="{ '--lleno': porcentaje }"
        :aria-label="t('marco.emision.volumen')"
        aria-orientation="vertical"
        :aria-valuetext="`${nivel} %`"
        @input="emit('update:modelValue', Number($event.target.value))"
        @pointerup="cerrarVolumen()"
        @keydown="alTeclearEnLinea"
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
@use "@/assets/scss/type" as t;

// `Estado=cerrado`: el icono a 22 en `icon/primary`, en una celda de 44 × 48.
// Al pasar el puntero y abierto, `icon/accent`.
.volumen {
  position: relative;
  display: flex;
  flex: none;
}

.volumen__icono {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: var(--alto-celda, 48px);
  padding: 0;
  border: 0;
  background: none;
  color: var(--color-icon-primary);
  cursor: pointer;

  &:hover,
  &[aria-expanded="true"] {
    color: var(--color-icon-accent);
  }

  &:focus-visible {
    outline: 2px solid var(--color-border-focus);
    outline-offset: -2px;
  }
}

// `Estado=abierto`: la caja flota encima del icono, a 8. 56 de ancho en
// `bg/canvas` con contorno `border/strong` a los cuatro lados, relleno 12 arriba
// y 14 abajo, 10 entre el número y la línea. **Flota**: excepción declarada en
// DESIGN.md § El volumen se despliega (Pablo, 20.10.26).
.volumen__caja {
  position: absolute;
  bottom: calc(100% + 8px);

  .volumen--abajo & {
    top: calc(100% + 8px);
    bottom: auto;
  }
  left: 50%;
  z-index: 5;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  box-sizing: border-box;
  width: 56px;
  padding: 12px 0 14px;
  border: 1px solid var(--color-border-strong);
  background: var(--color-bg-canvas);
  transform: translateX(-50%);
}

.volumen__numero {
  @include t.mono-micro;
  margin: 0;
  color: var(--color-text-secondary);
  font-variant-numeric: tabular-nums;
}

// La zona de 56 × 150 es el propio `range`, girado con `writing-mode` y el
// máximo arriba: se arrastra o se pulsa en cualquier punto. La pista de 3 en
// `border/strong`, el nivel lleno desde abajo en `action/primary/default` y el
// tirador de 16 × 8 en el mismo rojo.
.volumen__linea {
  writing-mode: vertical-lr;
  direction: rtl;
  width: 54px;
  height: 150px;
  margin: 0;
  background: transparent;
  appearance: none;
  cursor: pointer;

  &::-webkit-slider-runnable-track {
    width: 3px;
    height: 100%;
    margin: 0 auto;
    background: linear-gradient(
      to top,
      var(--color-action-primary-default) var(--lleno),
      var(--color-border-strong) var(--lleno)
    );
  }

  &::-moz-range-track {
    width: 3px;
    background: var(--color-border-strong);
  }

  &::-moz-range-progress {
    width: 3px;
    background: var(--color-action-primary-default);
  }

  &::-webkit-slider-thumb {
    width: 16px;
    height: 8px;
    margin-left: -6.5px;
    border: 0;
    border-radius: 0;
    background: var(--color-action-primary-default);
    appearance: none;
  }

  &::-moz-range-thumb {
    width: 16px;
    height: 8px;
    border: 0;
    border-radius: 0;
    background: var(--color-action-primary-default);
  }

  &:focus-visible {
    outline: 2px solid var(--color-border-focus);
    outline-offset: -2px;
  }
}
</style>
